import "./styles.scss";

import { Text } from "@fragments";
import {
  IconCalendarTime,
  IconPlayerPlayFilled,
  IconSchool,
  IconShare,
} from "@tabler/icons-react";

import {
  buildMentoringTimeline,
  formatTimelineDate,
  formatRelative,
} from "@pages/Mentoring/helpers/mentoringTimeline";

const MARKER_ICON = {
  start: IconPlayerPlayFilled,
  mentoring: IconSchool,
  split: IconShare,
};

/**
 * Single-axis mentoring timeline (§6.11). One track runs from the mentoring
 * start to the latest end (coprodução/split, or the mentoring end when there's
 * no split); the mentoring end is an intermediate marker. A blue fill grows to
 * the current moment (animated) and ends in a dot. Speech-bubble balloons — an
 * icon chip + label + date (+ relative time) — point to each marker via a dotted
 * connector. `compact` renders the scaled-down version (smaller balloons, short
 * labels, no relative time) for the list cards; the full version is used on the
 * single-mentoring detail. Both share the same balloon design.
 */
export default function MentoringTimeline({ timestamps = {}, compact = false }) {
  const data = buildMentoringTimeline(timestamps);

  if (!data) {
    return (
      <Text.span contrast={4} left={<IconCalendarTime size={14} />}>
        A mentoria ainda não começou.
      </Text.span>
    );
  }

  const { markers, nowPos } = data;

  return (
    <div className={`mt2${compact ? " mt2--compact" : ""}`}>
      <div className="mt2-stage">
        {markers.map((marker, index) => (
          <div className="mt2-marker" style={{ left: `${marker.pos}%` }} key={marker.key}>
            <div className="mt2-balloon" style={{ transform: balloonShift(marker.pos) }}>
              <span className="mt2-balloon-icon">
                <MarkerIcon kind={marker.kind} size={compact ? 15 : 17} />
              </span>
              <Text.span contrast={9} weight={500}>
                {compact ? marker.short : marker.label}
              </Text.span>
              <Text.span contrast={7}>{formatTimelineDate(marker.iso)}</Text.span>
              {!compact && (
                <Text.span contrast={4}>{formatRelative(marker.iso)}</Text.span>
              )}
            </div>
            <span className="mt2-connector" />
          </div>
        ))}
      </div>

      <Track nowPos={nowPos} markers={markers} />
    </div>
  );
}

function Track({ nowPos, markers }) {
  return (
    <div className="mt2-track-row">
      <div className="mt2-line">
        <span className="mt2-ticks" />
        <span className="mt2-fill" style={{ "--now": `${nowPos}%` }} />
        {markers.map((marker) =>
          marker.pos > 0 && marker.pos < 100 ? (
            <span
              className={`mt2-point mt2-point--${marker.kind}`}
              style={{ left: `${marker.pos}%` }}
              key={marker.key}
            />
          ) : null
        )}
        <span className="mt2-now" style={{ "--now": `${nowPos}%` }} />
      </div>
      <span className="mt2-cap mt2-cap--start" />
      <span className="mt2-cap mt2-cap--end" />
    </div>
  );
}

function MarkerIcon({ kind, size = 17 }) {
  const Icon = MARKER_ICON[kind] ?? IconCalendarTime;
  return <Icon size={size} />;
}

// Keeps the balloon inside the chart: flush-left near the start, flush-right near
// the end, centered in the middle. The icon stays centered on the balloon while
// the dotted connector points to the marker's true position on the track, so edge
// balloons connect from their bottom-left / bottom-right (as in the design).
function balloonShift(pos) {
  if (pos <= 15) return "translateX(-14px)";
  if (pos >= 85) return "translateX(calc(-100% + 14px))";
  return "translateX(-50%)";
}
